import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Video,
  Download,
  Share2,
  Clock,
  CheckCircle,
  XCircle,
  LayoutGrid,
  Play,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function VideoTile({ rec, onClick }) {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current) videoRef.current.play().catch(() => {});
  };
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const statusIcon = {
    completed: <CheckCircle className="w-3.5 h-3.5" />,
    failed: <XCircle className="w-3.5 h-3.5" />,
  };
  const statusColor = {
    completed: "bg-green-500/90 text-white",
    failed: "bg-red-500/90 text-white",
    pending: "bg-amber-500/90 text-white",
    processing: "bg-blue-500/90 text-white",
  };

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`video-tile-${rec.id}`}
    >
      <div className="aspect-[9/16] relative bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
        {rec.generated_video ? (
          <>
            <video
              ref={videoRef}
              src={rec.generated_video}
              className="w-full h-full object-cover"
              loop
              muted
              playsInline
            />
            {!isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-5 h-5 text-slate-700 ml-0.5" />
                </div>
              </div>
            )}
          </>
        ) : rec.generated_composite ? (
          <img src={rec.generated_composite} alt="Composite" className="w-full h-full object-cover" />
        ) : rec.status === "pending" || rec.status === "processing" ? (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">Processing...</p>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-10 h-10 text-muted-foreground" />
          </div>
        )}

        {/* Status badge */}
        <div className="absolute top-2 left-2">
          <Badge className={`text-[10px] ${statusColor[rec.status] || statusColor.pending}`}>
            <span className="flex items-center gap-1">
              {statusIcon[rec.status] || <Clock className="w-3.5 h-3.5" />}
              {rec.status}
            </span>
          </Badge>
        </div>
      </div>

      <div className="p-3">
        <p className="text-sm font-semibold truncate">{rec.template_title || rec.template_type?.toUpperCase()}</p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">{rec.aspect_ratio}</p>
          <p className="text-xs text-muted-foreground">{new Date(rec.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}

export default function MyRecreations() {
  const navigate = useNavigate();
  const { recreationId } = useParams();
  const { user } = useAuth();
  const [recreations, setRecreations] = useState([]);
  const [selectedRecreation, setSelectedRecreation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecreations();
  }, []);

  useEffect(() => {
    if (recreationId && recreations.length > 0) {
      const rec = recreations.find((r) => r.id === recreationId);
      if (rec) setSelectedRecreation(rec);
    } else if (!recreationId) {
      setSelectedRecreation(null);
    }
  }, [recreationId, recreations]);

  const fetchRecreations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/templates/user/recreations`, { withCredentials: true });
      setRecreations(response.data.recreations);

      if (recreationId) {
        const rec = response.data.recreations.find((r) => r.id === recreationId);
        if (rec) setSelectedRecreation(rec);
      }
    } catch (error) {
      console.error("Error fetching recreations:", error);
      toast.error("Failed to load recreations");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (videoUrl) => {
    try {
      toast.info("Downloading video...");
      const downloadUrl = `${API}/api/download-video?url=${encodeURIComponent(videoUrl)}`;
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = videoUrl.split("/").pop()?.split("?")[0] || "video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  // Detail View
  if (selectedRecreation) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="recreation-detail-view">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedRecreation(null);
              navigate("/my-recreations");
            }}
            className="mb-6"
            data-testid="back-to-grid-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Videos
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Video Player */}
            <div className="lg:col-span-3">
              <div className="aspect-[9/16] max-h-[75vh] mx-auto bg-black rounded-xl overflow-hidden">
                {selectedRecreation.generated_video ? (
                  <video
                    src={selectedRecreation.generated_video}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                ) : selectedRecreation.status === "pending" || selectedRecreation.status === "processing" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white">
                    <Loader2 className="w-12 h-12 animate-spin mb-4" />
                    <p className="text-lg font-medium">Processing...</p>
                    <p className="text-sm text-white/70">Your video is being created</p>
                  </div>
                ) : selectedRecreation.generated_composite ? (
                  <img src={selectedRecreation.generated_composite} alt="Composite" className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <Video className="w-16 h-16 opacity-50" />
                  </div>
                )}
              </div>
            </div>

            {/* Details Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Actions */}
              {selectedRecreation.generated_video && (
                <div className="flex flex-col gap-3">
                  <Button onClick={() => handleDownload(selectedRecreation.generated_video)} size="lg" className="w-full" data-testid="download-video-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download Video
                  </Button>
                  <Button variant="outline" size="lg" className="w-full">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              )}

              {/* Info */}
              <Card>
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-semibold text-base">Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Status</p>
                      <p className="font-medium flex items-center gap-1.5 mt-0.5">
                        {getStatusIcon(selectedRecreation.status)}
                        {selectedRecreation.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Created</p>
                      <p className="font-medium mt-0.5">{new Date(selectedRecreation.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Type</p>
                      <p className="font-medium mt-0.5">{selectedRecreation.template_type?.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Aspect Ratio</p>
                      <p className="font-medium mt-0.5">{selectedRecreation.aspect_ratio}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Script */}
              {selectedRecreation.generated_script && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-sm mb-2">Generated Script</h3>
                    <p className="text-sm text-muted-foreground italic leading-relaxed">
                      "{selectedRecreation.generated_script}"
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Error */}
              {selectedRecreation.error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Error</p>
                  <p className="text-sm text-red-600">{selectedRecreation.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="min-h-screen bg-gray-50" data-testid="recreations-grid-view">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Videos</h1>
            <p className="text-muted-foreground mt-1">{recreations.length} video{recreations.length !== 1 ? "s" : ""} created</p>
          </div>
          <Button onClick={() => navigate("/templates")} variant="outline" data-testid="browse-templates-btn">
            <LayoutGrid className="w-4 h-4 mr-2" />
            Browse Templates
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : recreations.length === 0 ? (
          <div className="text-center py-20">
            <Video className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No videos yet</h3>
            <p className="text-muted-foreground mb-6">Create your first video from a template</p>
            <Button onClick={() => navigate("/templates")}>
              <LayoutGrid className="w-4 h-4 mr-2" />
              Browse Templates
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {recreations.map((rec) => (
              <VideoTile
                key={rec.id}
                rec={rec}
                onClick={() => {
                  setSelectedRecreation(rec);
                  navigate(`/my-recreations/${rec.id}`);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
